import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import { landingPageTemplatesApi } from '@/api/landingPageTemplates'
import { useToast } from '@/hooks/useToast'
import { getDisplayErrorMessages, isValidationError } from '@/utils/errorHandling'
import type { LandingPageTemplateStatusDto } from '@/types/landingPage'

export interface UseLandingPageTemplateStatusResult {
    /** Null until loaded, and while `webId` is null. */
    status: LandingPageTemplateStatusDto | null
    loading: boolean
    error: string | null
    reload: () => Promise<void>
    /** Saves the page as a template, or renames it. Resolves false when refused; the failure has been shown. */
    saveTemplate: (name: string) => Promise<boolean>
    /** Stops the page being a template. Resolves false when refused; the failure has been shown. */
    removeTemplate: () => Promise<boolean>
}

/**
 * One web's landing page as the Save-as-template control needs it (ORB-1103). Pass `webId` null to load nothing,
 * for a web with no Builder landing page.
 */
export function useLandingPageTemplateStatus(workspaceId: string, webId: string | null): UseLandingPageTemplateStatusResult {
    const { showError } = useToast()
    const [status, setStatus] = useState<LandingPageTemplateStatusDto | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const requestIdRef = useRef(0)

    const reload = useCallback(async () => {
        if (!webId) {
            setLoading(false)
            return
        }
        const requestId = ++requestIdRef.current
        setLoading(true)
        setError(null)
        const res = await landingPageTemplatesApi.getTemplateStatus(workspaceId, webId)
        if (requestIdRef.current !== requestId) return
        if (res.data) setStatus(res.data)
        else setError(getDisplayErrorMessages(res.error, 'Unable to load the template status').join(' '))
        setLoading(false)
    }, [workspaceId, webId])

    useEffect(() => {
        setStatus(null)
        void reload()
        return () => { requestIdRef.current++ }
    }, [reload])

    // A 422 is the server's refusal, which the global handler has already shown in its own words.
    const reportFailure = useCallback((res: { error?: string; status?: number }, title: string, fallback: string) => {
        if (isValidationError(res.status)) return
        showError(title, getDisplayErrorMessages(res.error, fallback).join(' '))
    }, [showError])

    const saveTemplate = useCallback(async (name: string) => {
        if (!webId) return false
        const res = await landingPageTemplatesApi.saveTemplate(workspaceId, webId, { Name: name })
        if (!res.data) {
            reportFailure(res, 'Could not save the template', 'Unable to save the template')
            return false
        }
        setStatus(res.data)
        return true
    }, [workspaceId, webId, reportFailure])

    const removeTemplate = useCallback(async () => {
        if (!webId) return false
        const res = await landingPageTemplatesApi.removeTemplate(workspaceId, webId)
        if (res.status !== 204 && res.status !== 200) {
            reportFailure(res, 'Could not remove the template', 'Unable to remove the template')
            return false
        }
        setStatus(current => current && { ...current, Template: null })
        return true
    }, [workspaceId, webId, reportFailure])

    return { status, loading, error, reload, saveTemplate, removeTemplate }
}
